
import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { simplePostCall } from "../../api/ApiServices";
import ApiConfig from "../../api/ApiConfig";

const CreateBankType = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // if exists → update mode
  const isEditMode = !!id;

  /* ---------------- STATE ---------------- */
  const [formData, setFormData] = useState({
    account_holder: "",
    account_number: "",
    bank_name: "",
    swift_code: "",
    branch: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  /* ---------------- VALIDATION ---------------- */
  const validateForm = () => {
    const err = {};
    if (!formData.account_holder.trim())
      err.account_holder = "Account holder is required";
    if (!formData.account_number.trim())
      err.account_number = "Account number is required";
    if (!formData.bank_name.trim())
      err.bank_name = "Bank name is required";
    if (!formData.swift_code.trim())
      err.swift_code = "Swift/IFSC code is required";

    setErrors(err);
    return Object.keys(err).length === 0;
  };

  /* ---------------- INPUT CHANGE ---------------- */
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  /* ---------------- VIEW (EDIT PREFILL) ---------------- */
  const fetchBankDetails = async () => {
    const body = {
      customer_id: Number(localStorage.getItem("customer_id")),
      api_key: localStorage.getItem("api_key"),
      user_id: Number(localStorage.getItem("user_id")),
      language: "en",
      id: Number(id),
    };

    try {
      const res = await simplePostCall(
        ApiConfig.bankView,
        JSON.stringify(body)
      );

      if (res?.result && res.data?.length) {
        const bank = res.data[0];
        setFormData({
          account_holder: bank.bank_account_holder_name || "",
          account_number: bank.bank_account_number || "",
          bank_name: bank.bank_name || "",
          swift_code: bank.bank_swift_code || "",
          branch: bank.bank_branch || "",
        });
      } else {
        toast.error("Failed to load bank details");
      }
    } catch {
      toast.error("Network error");
    }
  };

  useEffect(() => {
    if (isEditMode) fetchBankDetails();
  }, [id]);

  /* ---------------- SUBMIT ---------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);

    const body = {
      customer_id: Number(localStorage.getItem("customer_id")),
      api_key: localStorage.getItem("api_key"),
      user_id: Number(localStorage.getItem("user_id")),
      language: "en",
      account_holder: formData.account_holder,
      bank_name: formData.bank_name,
      branch: formData.branch,
      account_number: formData.account_number,
      swift_code: formData.swift_code,
      ...(isEditMode && { id: Number(id) }),
    };

    const api = isEditMode
      ? ApiConfig.bankUpdate
      : ApiConfig.bankCreate;

    try {
      const res = await simplePostCall(api, JSON.stringify(body));
      if (res?.result) {
        toast.success(res.message);
        navigate("/accountant/BankType");
      } else {
        toast.error(res?.message || "Operation failed");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <div className="income-type-container">
      <div className="special-header panel-white-all">
        <h4 className="page-title">
          {isEditMode ? "Update Bank Details" : "Create Bank Details"}
        </h4>
      </div>

      <div className="yc-list-body">
        <div className="card">
          <div className="card-body">
            <Form onSubmit={handleSubmit}>
          <Row className="g-3 mt-2">
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Account Holder Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  name="account_holder"
                  value={formData.account_holder}
                  onChange={handleChange}
                  isInvalid={!!errors.account_holder}
                  type="text"
                  placeholder="Enter account holder name"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.account_holder}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Account Number <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  name="account_number"
                  value={formData.account_number}
                  onChange={handleChange}
                  isInvalid={!!errors.account_number}
                  type="text"
                  placeholder="Enter account number"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.account_number}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Bank Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  name="bank_name"
                  value={formData.bank_name}
                  onChange={handleChange}
                  isInvalid={!!errors.bank_name}
                  type="text"
                  placeholder="Enter bank name"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.bank_name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Swift Code / IBAN / IFSC{" "}
                  <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  name="swift_code"
                  value={formData.swift_code}
                  onChange={handleChange}
                  isInvalid={!!errors.swift_code}
                  type="text"
                  placeholder="Enter code"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.swift_code}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Branch</Form.Label>
                <Form.Control
                  name="branch"
                  value={formData.branch}
                  onChange={handleChange}
                  type="text"
                  placeholder="Enter branch"
                />
              </Form.Group>
            </Col>
          </Row>

          <div className="mt-4 d-flex justify-content-end">
            <Button type="submit" disabled={loading} className="btn btn-primary">
              {loading ? <Spinner size="sm" /> : isEditMode ? "Update" : "Create"}
            </Button>
          </div>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateBankType;
